<template>
    <div
        v-loading="actionInProgress"
        class="well fs-3 cart-item"
        :class="{ 'cart-item-done': isCartCanceled || isCartPaid }"
    >
        <div class="flex-level">
            <h3 class="m-0">
                {{
                    $filters.capitalize(
                        $l10n.__('panier %(context)s', {
                            context: $filters.capitalize(cart.context),
                        }),
                    )
                }}
                {{
                    cart.context.includes('renew') && !!cart.earlyRenewal
                        ? $l10n.__('anticipé')
                        : ''
                }}
            </h3>
            <span class="mt-1 ml-2 gray-80 semibold">
                {{ $l10n.__('modifié') }} {{ formatDate(cart.updatedAt, true, true) }}
            </span>
            <span :class="'flex-right semibold label label-' + getCartStatusData(cartStatus).css">
                {{ $filters.capitalize(getCartStatusData(cartStatus).label) }}
                {{ isCartExpired ? ' (' + $l10n.__('expiré') + ')' : '' }}
            </span>
            <div v-if="cartActions.length > 0">
                <DropdownMultiLevel
                    :actions="cartActions"
                    :permissions="permissions"
                    @clicked="switchAction"
                />
            </div>
        </div>
        <el-row>
            <el-col :span="7">
                <div class="pr-3 mr-3 br-2">
                    <h4 class="my-2 semibold">
                        {{ $filters.capitalize($l10n.__('informations')) }}:
                    </h4>

                    <line-2-slots>
                        <!-- SOURCE -->
                        <template #left>{{ $filters.capitalize($l10n.__('source')) }}</template>
                        <template #right>{{ $filters.capitalize(cart.source) }}</template>
                    </line-2-slots>
                    <line-2-slots v-if="cart.source == 'sales'" class="mb-2">
                        <!-- SALES -->
                        <template #left>{{ $filters.capitalize($l10n.__('commercial')) }}</template>
                        <template #right>{{
                            $filters.capitalize(`${cart.ownerFirstName} ${cart.ownerName}`)
                        }}</template>
                    </line-2-slots>
                    <line-2-slots>
                        <!-- PRICING -->
                        <template #left>{{ $filters.capitalize($l10n.__('pricing')) }}</template>
                        <template #right
                            >v.{{ cart.pricingVersion }} (rev.
                            {{ cart.pricingVersionRevision }})</template
                        >
                    </line-2-slots>
                    <line-2-slots class="mb-2">
                        <!-- PLAN -->
                        <template #left>{{ $filters.capitalize($l10n.__('plan')) }}</template>
                        <template #right>{{ cart.pricingPlanId }}</template>
                    </line-2-slots>

                    <line-2-slots>
                        <!-- CREATION DATE -->
                        <template #left>{{ $filters.capitalize($l10n.__('créé le')) }}</template>
                        <template #right>
                            {{ formatDate(cart.createdAt, true, true) }}
                        </template>
                    </line-2-slots>
                    <line-2-slots
                        v-if="!isCartValidating && !isCartValidated && !isCartPaid"
                        :isSubtitle="isCartExpired"
                    >
                        <!-- EXPIRATION DATE -->
                        <template #left>{{ $filters.capitalize($l10n.__('expire le')) }}</template>
                        <template #right>
                            <i v-if="isCartExpired" class="chip chip-error"></i>
                            {{ formatDate(cart.expirationDate) }}
                        </template>
                    </line-2-slots>
                    <line-2-slots v-if="cart.periodApplianceStart">
                        <!-- PERIOD START DATE -->
                        <template #left>{{
                            $filters.capitalize($l10n.__("début d'application"))
                        }}</template>
                        <template #right>{{ formatDate(cart.periodApplianceStart) }}</template>
                    </line-2-slots>
                    <line-2-slots v-if="cart.periodApplianceStop">
                        <!-- PERIOD END DATE -->
                        <template #left>
                            {{ $filters.capitalize($l10n.__("fin d'application")) }}
                        </template>
                        <template #right>{{ formatDate(cart.periodApplianceStop) }}</template>
                    </line-2-slots>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="pr-3 mr-3 br-2">
                    <h4 class="my-2 semibold">{{ $filters.capitalize($l10n.__('contenu')) }}:</h4>
                    <ul class="m-0">
                        <li v-for="item in itemsOrdered" :key="item.id" class="flex-level">
                            <!-- PRODUCT NAME -->
                            <span class="flex-1">{{ product(item.product).printableName }}</span>
                            <span class="flex-item-right flex-2 px-2">
                                <!-- QUANTITY -->
                                <span
                                    v-if="productPlan(item.product).licensed"
                                    class="label label-danger p-1 fs-2 semibold"
                                    >x{{ item.quantity }}</span
                                >
                                <span
                                    v-if="
                                        productPlan(item.product).licensed &&
                                        itemFreePricingTier(item)
                                    "
                                    class="label label-success p-1 fs-2 semibold"
                                    >{{
                                        $l10n.n__(
                                            'dont %(offered)s offert',
                                            'dont %(offered)s offerts',
                                            itemFreePricingTier(item),
                                            { offered: itemFreePricingTier(item) },
                                        )
                                    }}</span
                                >
                            </span>
                            <!-- AMOUNT WITHOUT DISCOUNT -->
                            <span
                                v-if="
                                    !itemHasPricingTier(item) &&
                                    amountPerMonth(item, true) !== amountPerMonth(item)
                                "
                                class="flex-item-right flex-1 aright strike gray-60 mr-1"
                                >{{
                                    $filters.numberFormatWithOptions(
                                        amountPerMonth(item),
                                        { precision: 2 },
                                        true,
                                    )
                                }}</span
                            >
                            <span
                                v-if="!itemHasPricingTier(item)"
                                class="flex-item-right flex-1 aright semibold"
                            >
                                {{
                                    $filters.numberFormatWithOptions(
                                        amountPerMonth(item, true),
                                        { precision: 2 },
                                        true,
                                    )
                                }}
                            </span>

                            <ul v-else class="aright bb-1">
                                <li v-for="tier in itemPricingTier(item)" :key="tier.up_to">
                                    <span v-if="tier.unit_amount == 0">
                                        {{
                                            $l10n.n__(
                                                '%(users)s utilisateur offert',
                                                '%(users)s utilisateurs offerts',
                                                tier.qty,
                                                { users: tier.qty },
                                            )
                                        }}
                                    </span>
                                    <span v-else-if="tier.up_to !== 'inf'">
                                        {{
                                            $l10n.n__(
                                                '%(users)s utilisateur :',
                                                '%(users)s utilisateurs :',
                                                tier.qty,
                                                { users: tier.qty },
                                            )
                                        }}
                                        <span
                                            v-if="
                                                currencyAmount(tier.unit_amount) !==
                                                amountPerMonth(item)
                                            "
                                            class="strike gray-60 mr-1"
                                        >
                                            {{
                                                $filters.numberFormatWithOptions(
                                                    amountPerMonth(item),
                                                    { precision: 2 },
                                                    true,
                                                )
                                            }}
                                        </span>
                                        {{
                                            $filters.numberFormatWithOptions(
                                                currencyAmount(tier.unit_amount),
                                                { precision: 2 },
                                                true,
                                            )
                                        }}
                                    </span>
                                    <span v-else>
                                        {{
                                            $l10n.n__(
                                                '%(users)s utilisateur :',
                                                '%(users)s utilisateurs :',
                                                item.quantity - tier.from,
                                                { users: item.quantity - tier.from },
                                            )
                                        }}
                                        <span
                                            v-if="
                                                currencyAmount(tier.unit_amount) !==
                                                amountPerMonth(item)
                                            "
                                            class="strike gray-60 mr-1"
                                            >{{
                                                $filters.numberFormatWithOptions(
                                                    amountPerMonth(item),
                                                    { precision: 2 },
                                                    true,
                                                )
                                            }}</span
                                        >
                                        {{
                                            $filters.numberFormatWithOptions(
                                                currencyAmount(tier.unit_amount),
                                                { precision: 2 },
                                                true,
                                            )
                                        }}
                                    </span>
                                </li>
                                <li v-if="productPlan(item.product).licensed" class="semibold">
                                    {{ $l10n.__('Total') }}:
                                    <span
                                        v-if="
                                            item.quantity * amountPerMonth(item) !==
                                            currencyAmount(item.totalAmount)
                                        "
                                        class="grey strike mr-1"
                                    >
                                        {{
                                            $filters.numberFormatWithOptions(
                                                item.quantity * amountPerMonth(item),
                                                { precision: 2 },
                                                true,
                                            )
                                        }}
                                    </span>
                                    {{
                                        $filters.numberFormatWithOptions(
                                            currencyAmount(item.totalAmount),
                                            { precision: 2 },
                                            true,
                                        )
                                    }}
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
            </el-col>
            <el-col :span="7">
                <div>
                    <h4 class="my-2 semibold">{{ $filters.capitalize($l10n.__('totaux HT')) }}:</h4>
                    <line-2-slots>
                        <template #left>{{ $filters.capitalize($l10n.__('par an')) }}</template>
                        <template #right>
                            {{ $filters.numberFormat(cart.totalAmountPerYear / 100, true) }}
                        </template>
                    </line-2-slots>
                    <line-2-slots>
                        <template #left>{{ $filters.capitalize($l10n.__('par mois')) }}</template>
                        <template #right>
                            {{ $filters.numberFormat(cart.totalAmountPerMonth / 100, true) }}
                        </template>
                    </line-2-slots>

                    <!-- PAYMENT FACILITIES -->
                    <line-2-slots v-if="isPaymentFacilityEnabled" :isSubtitle="true" class="mt-2">
                        <template #left>
                            {{ $l10n.__('Paiement en %s fois activé', cartNbFacilities) }}
                        </template>
                        <template v-if="cartNextAmountFacility < cartFirstAmountFacility" #right>
                            1 x {{ formatAmount(cartFirstAmountFacility, true) }}
                        </template>
                        <template v-else #right>
                            {{ cartNbFacilities }} x
                            {{ $filters.numberFormat(cartFirstAmountFacility, true) }}
                        </template>
                    </line-2-slots>
                    <line-2-slots
                        v-if="
                            isPaymentFacilityEnabled &&
                            cartNextAmountFacility < cartFirstAmountFacility
                        "
                    >
                        <template #right>
                            {{ cartNbFacilities - 1 }} x
                            {{ $filters.numberFormat(cartNextAmountFacility, true) }}
                        </template>
                    </line-2-slots>

                    <line-2-slots
                        v-if="isCustomPaymentFacilityEnabled"
                        :isSubtitle="true"
                        class="mt-2"
                    >
                        <template #left>
                            {{ $l10n.__('Paiement en %s fois activé', cartNbFacilities) }}
                        </template>
                        <template
                            v-if="cartFirstAmountCustomFacility < cartLastAmountCustomFacility"
                            #right
                        >
                            {{ cartNbFirstCustomFacilities }} x
                            {{ formatAmount(cartFirstAmountCustomFacility, true) }}
                        </template>
                        <template v-else #right>
                            {{ cartNbCustomFacilities }} x
                            {{ formatAmount(cartFirstAmountFacility, true) }}
                        </template>
                    </line-2-slots>
                    <line-2-slots
                        v-if="
                            isCustomPaymentFacilityEnabled &&
                            cartFirstAmountCustomFacility < cartLastAmountCustomFacility
                        "
                    >
                        <template #right>
                            1 x {{ formatAmount(cartLastAmountCustomFacility, true) }}
                        </template>
                    </line-2-slots>

                    <!-- PAYMENT METHOD -->
                    <line-2-slots v-if="cart.paymentMethod" class="mt-2">
                        <template #left>{{ $filters.capitalize($l10n.__('paiement')) }}</template>
                        <template #right>{{
                            $filters.capitalize(
                                paymentMethodsOptions.find(
                                    (method) => method.value == cart.paymentMethod,
                                ).label,
                            )
                        }}</template>
                    </line-2-slots>
                </div>
            </el-col>
        </el-row>

        <div v-if="canShowCheckoutUrl" class="flex-level pt-3">
            <!-- LINK -->
            <h4 class="m-0">{{ $l10n.__('Lien de paiement :') }}</h4>
            <a :href="currentCheckoutUrlForCustomer" target="_blank" class="flex mx-3">
                <TextInput
                    mode="edit"
                    :modelValue="currentCheckoutUrlForCustomer"
                    :readonly="true"
                />
            </a>
            <CompassButton
                type="secondary"
                @click="handleCopyToClipboardWithToast(currentCheckoutUrlForCustomer)"
            >
                {{ $l10n.__('Copier') }}
            </CompassButton>
        </div>
    </div>
</template>
<script>
import Line2Slots from '../utilities/Line2slots.vue';
import WidgetMixins from '../../mixins/sellsyaccountmanager/widgetMixins.js';
import SAMMixins from '../../mixins/subscription/SAMMixins.js';
import DropdownMultiLevel from 'vcomponents/utilities/DropdownMultiLevel.vue';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import { handleCopyToClipboardWithToast } from 'Helpers/stringHelper';

export default {
    components: {
        Line2Slots,
        DropdownMultiLevel,
        TextInput,
    },
    mixins: [WidgetMixins, SAMMixins],
    props: {
        corporation: {
            type: String,
            required: true,
        },
        cart: {
            type: Object,
            required: true,
        },
        permissions: {
            type: Object,
        },
    },
    data() {
        return {
            cartStatus: '',
            actionInProgress: false,
        };
    },
    computed: {
        // FORMATTERS
        itemsOrdered() {
            const items = [];
            this.pricingProducts.forEach((product) => {
                const item = this.cart.items.find((i) => i.product === product.id);
                if (item == undefined) return;
                items.push(item);
            });
            return items;
        },
        cartUrl() {
            if (this.cart.pricingVersion === 4) {
                switch (this.cart.context) {
                    case 'upsale':
                        return this.$router.resolve({
                            name: 'sam.v4.upsell.cart.form',
                            params: {
                                corpId: this.corporation,
                                thirdId: this.customer.saasThirdId,
                                customerId: this.customer.id,
                                id: this.cart.id,
                            },
                        }).href;
                    case 'renew':
                        return this.$router.resolve({
                            name: 'sam.v4.sale.renew.cart.form',
                            params: {
                                corpId: this.corporation,
                                thirdId: this.customer.saasThirdId,
                                customerId: this.customer.id,
                                id: this.cart.id,
                            },
                        }).href;
                    case 'legacy_renew':
                        return this.$router.resolve({
                            name: 'sam.v4.sale.renew.cart.form',
                            query: { isLegacy: true },
                            params: {
                                corpId: this.corporation,
                                thirdId: this.$route.params.id,
                                customerId: this.cart.customer.uuid,
                                id: this.cart.id,
                            },
                        }).href;
                    default:
                        return this.$router.resolve({
                            name: 'sam.v4.cart.form',
                            params: {
                                thirdId: this.customer.saasThirdId,
                                customerId: this.customer.id,
                                id: this.cart.id,
                            },
                        }).href;
                }
            } else {
                return this.buildUrlCartSale(this.corporation, this.cart.id);
            }
        },
        cartInvoiceOverviewUrl() {
            if (this.cart.externalInvoiceId === null) return false;
            return this.getRouteByName('invoicing.invoice.overview', [
                { key: 'id', value: this.cart.externalInvoiceId },
            ]);
        },
        // PAYMENT FACILITIES
        isCurrentPricingMonthly() {
            return this.cart.monthlyPlan;
        },
        isPaymentFacilityEnabled() {
            return (
                !this.isCurrentPricingMonthly &&
                this.cart.paymentFacility > 1 &&
                this.cart.paymentFacility < 12 &&
                ['new', 'renew', 'legacy_renew'].includes(this.cart.context)
            );
        },
        cartFacilityAmounts() {
            return this.buildFacilityAmounts(
                this.cart.totalAmountPerYear / 100,
                this.cart.paymentFacility,
            );
        },
        cartFirstAmountFacility() {
            return this.cartFacilityAmounts.firstAmount;
        },
        cartNextAmountFacility() {
            return this.cartFacilityAmounts.nextAmount;
        },
        isCustomPaymentFacilityEnabled() {
            return !this.isCurrentPricingMonthly && this.cart.paymentFacility > 10000;
        },
        cartCustomFacilitiesAmount() {
            return this.buildCustomFacilityAmounts(
                this.cart.totalAmountPerYear / 100,
                this.cart.paymentFacility,
            );
        },
        cartNbFirstCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities;
        },
        cartNbCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities + 1;
        },
        cartFirstAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.firstAmount;
        },
        cartLastAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.lastAmount;
        },
        cartNbFacilities() {
            if (this.isCurrentPricingMonthly) {
                return 1;
            }

            return this.isCustomPaymentFacilityEnabled
                ? this.cartNbCustomFacilities
                : this.cart.paymentFacility;
        },
        // CHECKS
        isCartDraft() {
            return this.cartStatus == 'draft';
        },
        isCartEstimate() {
            return this.cartStatus == 'estimate';
        },
        isCartValidating() {
            return this.cartStatus == 'validating';
        },
        isCartValidated() {
            return this.cartStatus == 'validated';
        },
        isCartPaid() {
            return this.cartStatus == 'paid';
        },
        isCartCanceled() {
            return this.cartStatus == 'canceled';
        },
        isCartExpired() {
            return (
                ['draft', 'estimate'].includes(this.cart.statusCode) &&
                moment(this.cart.expirationDate).isBefore()
            );
        },
        canAccessToCartWidget() {
            return this.isCartDraft || this.isCartEstimate;
        },
        // ACTIONS
        cartActions() {
            const actions = [];

            if (this.permissions.sam_can_create_cart) {
                actions.push({
                    label: this.$l10n.__('Éditer'),
                    type: 'link',
                    value: this.cartUrl,
                    permission: this.canAccessToCartWidget,
                });
            }
            if (this.isCartPaid && this.permissions.can_access_to_invoice) {
                actions.push({
                    label: this.$l10n.__('Accéder à la facture'),
                    type: 'link',
                    value: this.cartInvoiceOverviewUrl,
                    permission: !!this.cartInvoiceOverviewUrl,
                });
            }

            if (this.isCartValidated && this.permissions.can_mark_cart_as_paid) {
                actions.push({
                    label: this.$l10n.__('Marquer comme payé'),
                    type: 'vuejs',
                    value: 'confirmMarkCartAsPaid',
                });
            }

            if (
                (this.isCartValidating || this.isCartValidated) &&
                this.permissions.can_mark_cart_as_draft
            ) {
                actions.push({
                    label: this.$l10n.__('Remettre en brouillon'),
                    type: 'vuejs',
                    value: 'confirmMarkCartAsDraft',
                });
            }
            if (this.cart.isCancelable && this.permissions.sam_can_create_cart) {
                actions.push({
                    label: this.$l10n.__('Supprimer'),
                    type: 'vuejs',
                    value: 'confirmCartDeletion',
                });
            }

            return actions;
        },
        canShowCheckoutUrl() {
            if (['new', 'legacy_renew'].includes(this.cart.context) && this.isCartValidated)
                return true;
            if (this.cart.paymentMethod == 'manual') {
                return this.isCartValidated;
            } else {
                return this.isCartValidating;
            }
        },
        currentCheckoutUrlForCustomer() {
            const nameRoute =
                this.cart.pricingVersion === 4
                    ? 'sam.v4.selfservice.cart.checkout'
                    : 'sam.selfservice.cart.checkout';
            const route = this.$router.resolve({ name: nameRoute }).resolved;
            return this.buildRoute(route, [{ key: 'id', value: this.cart.id }], true);
        },
        cartCanBeMarkAsDraft() {
            return (
                (this.isCartValidating || this.isCartValidated) &&
                !['renew'].includes(this.cart.context)
            );
        },
    },
    created() {
        this.cartStatus = this.cart.statusCode;
    },
    methods: {
        handleCopyToClipboardWithToast,
        product(id) {
            return this.pricingProducts.find((product) => product.id === id);
        },
        productPlan(id) {
            if (this.product(id) === undefined) return;
            return this.getProductPlan(this.product(id), this.cart.pricingPlanId);
        },
        itemHasPricingTier(item) {
            return null !== item.customPricing && item.customPricing.length > 1;
        },
        itemFreePricingTier(item) {
            if (!this.itemHasPricingTier(item)) return 0;
            const freeTier = this.itemPricingTier(item).find((tier) => tier.unit_amount == 0);
            return undefined !== freeTier ? freeTier.qty : 0;
        },
        itemPricingTier(item) {
            const customPricing = [];
            let previousTier;
            _.each(item.customPricing, (tier) => {
                const current = _.cloneDeep(tier);
                current.from = previousTier?.up_to ?? 0;
                current.qty = _.isNumber(tier.up_to) ? tier.up_to - current.from : tier.up_to;
                previousTier = tier;
                customPricing.push(current);
            });
            return customPricing;
        },
        currencyAmount(amount) {
            return this.centsToCurrency(amount) / (!this.isCurrentPricingMonthly ? 12 : 1);
        },
        amountPerMonth(item, withCustomPricing) {
            let total = item.totalAmountPerMonth; // total discounted
            const quantity = item.quantity;
            if (!withCustomPricing) total += item.totalDiscountAmountPerMonth;
            return total / quantity / 100;
        },
        switchAction(action) {
            switch (action) {
                case 'confirmMarkCartAsPaid':
                    return this.$confirm(
                        this.$l10n.__('Confirmer cette action passera le panier en statut "payé"'),
                        this.$l10n.__('Marquer le panier comme étant payé'),
                        {
                            cancelButtonText: this.$l10n.__('Annuler'),
                            confirmButtonText: this.$l10n.__('Confirmer'),
                            confirmButtonClass: 'btn btn-primary',
                            type: 'success',
                        },
                    )
                        .then(() => {
                            this.actionInProgress = true;
                            return this.confirmMarkCartAsPaid(this.cart);
                        })
                        .catch(() => {})
                        .finally(() => (this.actionInProgress = false));
                    break;
                case 'confirmMarkCartAsDraft':
                    return this.$confirm(
                        this.$l10n.__(
                            'Confirmer cette action passera le panier en statut "brouillon"',
                        ),
                        this.$l10n.__('Passer le panier en brouillon'),
                        {
                            cancelButtonText: this.$l10n.__('Annuler'),
                            confirmButtonText: this.$l10n.__('Confirmer'),
                            confirmButtonClass: 'btn btn-primary',
                            type: 'success',
                        },
                    )
                        .then(() => {
                            this.actionInProgress = true;
                            return this.confirmMarkCartAsDraft(this.cart);
                        })
                        .catch(() => {})
                        .finally(() => (this.actionInProgress = false));
                    break;
                case 'confirmCartDeletion':
                    return this.$confirm(
                        this.$l10n.__('Souhaitez-vous vraiment supprimer ce panier ?'),
                        this.$l10n.__('Suppression du panier'),
                        {
                            cancelButtonText: this.$l10n.__('Annuler'),
                            confirmButtonText: this.$l10n.__('Confirmer la suppression'),
                            confirmButtonClass: 'btn btn-primary',
                            type: 'warning',
                        },
                    )
                        .then(() => {
                            this.actionInProgress = true;
                            return this.confirmCartDeletion(this.cart);
                        })
                        .catch(() => {})
                        .finally(() => (this.actionInProgress = false));
                    break;
            }
        },
        confirmMarkCartAsPaid(cart) {
            return this.markCartAsPaid(cart.id)
                .then((cart) => {
                    this.cartStatus = cart.statusCode;
                    Push.success(this.$l10n.__('Statut du panier mis à jour'));
                })
                .catch((e) => {
                    switch (e.error.message) {
                        case 'cart_not_validated':
                            Push.error(
                                this.$l10n.__("La panier n'est pas en statut %(stepName)s", {
                                    stepName: this.$l10n.__('validé'),
                                }),
                            );
                            break;
                        case 'cart_same_status':
                            Push.error(this.$l10n.__('Le statut du panier est identique'));
                            break;
                        case 'cart_not_found':
                            Push.error(this.$l10n.__('Panier introuvable'));
                            break;
                        case 'cart_forbidden':
                            Push.error(this.$l10n.__("L'accès à ce panier n'est pas autorisé"));
                            break;
                        default:
                            Push.error(
                                this.$l10n.__('Erreur lors de la mise à jour du statut du panier'),
                            );
                            break;
                    }
                });
        },
        confirmMarkCartAsDraft(cart) {
            return this.markCartAsDraft(cart.id)
                .then((cart) => {
                    this.cartStatus = cart.statusCode;
                    Push.success(this.$l10n.__('Statut du panier mis à jour'));
                })
                .catch((e) => {
                    switch (e.error.message) {
                        case 'cart_not_validated':
                            Push.error(
                                this.$l10n.__("La panier n'est pas en statut %(stepName)s", {
                                    stepName: this.$l10n.__('validé'),
                                }),
                            );
                            break;
                        case 'cart_same_status':
                            Push.error(this.$l10n.__('Le statut du panier est identique'));
                            break;
                        case 'cart_not_found':
                            Push.error(this.$l10n.__('Panier introuvable'));
                            break;
                        case 'cart_forbidden':
                            Push.error(this.$l10n.__("L'accès à ce panier n'est pas autorisé"));
                            break;
                        default:
                            Push.error(
                                this.$l10n.__('Erreur lors de la mise à jour du statut du panier'),
                            );
                            break;
                    }
                });
        },
        confirmCartDeletion(cart) {
            return this.cancelCart(cart.id)
                .then((cart) => {
                    this.cartStatus = 'canceled';
                    this.cart.isCancelable = false;
                    Push.success(this.$l10n.__('Statut du panier mis à jour'));
                })
                .catch((e) => {
                    switch (e.error.message) {
                        case 'cart_not_validated':
                            Push.error(
                                this.$l10n.__("La panier n'est pas en statut %(stepName)s", {
                                    stepName: this.$l10n.__('validé'),
                                }),
                            );
                            break;
                        case 'cart_same_status':
                            Push.error(this.$l10n.__('Le statut du panier est identique'));
                            break;
                        case 'cart_not_found':
                            Push.error(this.$l10n.__('Panier introuvable'));
                            break;
                        case 'cart_forbidden':
                            Push.error(this.$l10n.__("L'accès à ce panier n'est pas autorisé"));
                            break;
                        default:
                            Push.error(
                                this.$l10n.__('Erreur lors de la mise à jour du statut du panier'),
                            );
                            break;
                    }
                });
        },
    },
};
</script>
<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;
.cart-item-hover:hover {
    background: $primary-05;
}
.cart-item-done {
    background: $gray-0;
}
</style>
